import AbstractStore from '@common/store/AbstractStore';
import LanguageRepository from '@common/plugins/Api/LanguageRepository';
import ReferentialVersion from '@common/store/ReferentialVersion';
import { readCached, writeCached } from '@common/tools/BootstrapCache';

interface LanguageState {
  locale: string;
  languages: LanguageInterface[];
  localTranslations: DataObjectInterface;
}

class Language extends AbstractStore<LanguageState> {
  protected data(): LanguageState {
    return {
      locale: localStorage.getItem('locale') || navigator.language.substr(0, 2),
      languages: [],
      localTranslations: {},
    };
  }

  protected initialize(): void {
    if (!ReferentialVersion.isExpected) {
      this.load();

      return;
    }

    ReferentialVersion.whenKnown().then((version) => {
      const cached = readCached<LanguageInterface[]>('languages', version);
      if (!cached) {
        this.load();

        return;
      }
      this.state.languages = cached;
      this.setInitialized(true);
    });
  }

  public load(): Promise<void> {
    if (this.loading) {
      return Promise.resolve();
    }

    this.loading = true;

    return LanguageRepository.get(new FormData())
      .then((languages: LanguageInterface[]) => {
        this.state.languages = languages;
        writeCached('languages', ReferentialVersion.getState().version, languages);
      })
      .finally(() => {
        this.setInitialized(true);
        this.loading = false;
      });
  }

  public get locale(): string {
    return this.getState().locale;
  }

  public set locale(locale: string) {
    localStorage.setItem('locale', locale);
    this.state.locale = locale;

    const language = this.getState().languages.find((l) => locale === l.locale) || null;
    if (!language) {
      return;
    }
    LanguageRepository.change(language);
  }

  public get currentLanguage(): LanguageInterface|null {
    return this.getState().languages.find(({ locale, uuid }) => (
      locale === this.locale || uuid === this.locale)) || null;
  }

  public get translations(): TranslationInterface[] {
    return this.currentLanguage?.translations || [];
  }

  public capitalize(s: string): string {
    return String(s[0]).toUpperCase() + String(s).slice(1).toLowerCase();
  }

  public translate(slug: string, domain = 'messages') {
    if (!this.isInitialized) {
      return slug;
    }

    if (this.state.localTranslations[`${slug}-${domain}`]) {
      return this.state.localTranslations[`${slug}-${domain}`].toString();
    }


    const pascalCase = slug.split('-').map((w, index) => !index ? w.toLowerCase() : this.capitalize(w)).join('').toUpperCase();
    const kebabCase = slug.replace(/([a-z])([A-Z])/g, "$1-$2").replace(/[\s_]+/g, '-').toUpperCase();
    const slugToUpper = slug.toUpperCase();

    let find = this.translations
      .find((t) => [slugToUpper, pascalCase, kebabCase].includes(t.slug.toUpperCase()) && t.domain === domain);
    if (!find) {
      find = this.translations.find((t) => [slugToUpper, pascalCase, kebabCase].includes(t.slug.toUpperCase()));
    }

    this.state.localTranslations[`${slug}-${domain}`] = find?.translation || slug || '';

    return this.state.localTranslations[`${slug}-${domain}`];
  }

  public findLanguage(value: string): LanguageInterface|null {
    return this.getState().languages.find(({ uuid }) => uuid === value) || null;
  }

  public findLanguageByLocale(value: string): LanguageInterface|null {
    return this.getState().languages.find(({ locale }) => locale === value) || null;
  }

  public translateMessage(message: string): string | null {
    const parts = message.split(' ');
    let translatedMessage = '';
    parts.forEach((part) => {
      if (this.translate(part).length) {
        translatedMessage += `${this.translate(part)} `;
      }
      if (!this.translate(part).length) {
        translatedMessage += `${part} `;
      }
    });

    return translatedMessage.charAt(0).toUpperCase() + translatedMessage.toLowerCase().slice(1);
  }

  public getSelectOptions(translatable = true): SelectOptionInterface[] {
    return this.getState().languages
      .filter((language) => (!translatable ? true : language.isTranslatable))
      .map((language) => ({
        value: language.locale,
        label: language.name,
      }));
  }

  public getSelectOptionsWithUuid(translatable = true): SelectOptionInterface[] {
    return this.getState().languages
      .filter((language) => (!translatable ? true : language.isTranslatable))
      .map((language) => ({
        value: language.uuid,
        label: language.name,
      }));
  }
}

export default new Language();
